import { yupResolver } from "@hookform/resolvers/yup";
import { InputLabel, LinearProgress, makeStyles, MenuItem, Select, Switch, Typography } from "@material-ui/core";
import React, { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";
import { useGetCandidatesValidQuery } from "../../../api/rtk/CandidateApi";
import { useGetExamSessionAvailableForSignUpQuery } from "../../../api/rtk/SignUpApi";
import {
  CourseForSignupViewModel,
  ModuleForSignupViewModel,
} from "../../../model/ExamSignup/ExamSessionForSignupViewModel";
import { GetTranslatedValueIntl } from "../../../model/Shared/TranslatedValue";
import CustomDraggableDialog from "../../common/customDialog/CustomDraggableDialog";
import CustomSelect, { OptionType } from "../../common/customSelect/CustomSelect";
import TranslatedMessage from "../../translations/TranslatedMessage";
import { Language, LanguageLabels } from "../../../data/constants";
import FormGridCell from "../../common/FormGridCell";
import LoadingIndicator from "../../common/LoadingIndicator";

interface ExamSessionCandidateAddDialogProps {
  examSessionId: string;
  isOpen: boolean;
  add: (candidateId: string, module: ModuleForSignupViewModel) => void;
  cancel: () => void;
}

interface ExamSessionCandidateAddDialogFormData {
  candidateId?: string;
  courseId?: string;
  moduleId?: string;
  bookingWithInvoice: boolean;
}

const formDefaultValues: ExamSessionCandidateAddDialogFormData = {
  bookingWithInvoice: true,
};

const formSchema = yup
  .object()
  .shape({
    candidateId: yup.string().required(),
    courseId: yup.string().required(),
    moduleId: yup.string().required(),
    bookingWithInvoice: yup.boolean().required().default(true),
  })
  .defined();

const useStyles = makeStyles(() => ({
  form: {
    marginBottom: 16,
  },
  candidateSelect: {
    minWidth: 500,
  },
  checkbox: {
    display: "flex",
    alignItems: "center",
  },
  languageCell: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  select: {
    width: "100%",
  },
}));

const ExamSessionCandidateAddDialog: FC<ExamSessionCandidateAddDialogProps> = ({
  examSessionId,
  isOpen,
  add,
  cancel,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const { handleSubmit, reset, resetField, watch, control } = useForm({
    mode: "onBlur",
    defaultValues: formDefaultValues,
    resolver: yupResolver(formSchema),
  });
  const candidateId = watch("candidateId");
  const courseId = watch("courseId");
  const moduleId = watch("moduleId");
  const bookingWithInvoice = watch("bookingWithInvoice");
  const { data: candidates, isFetching } = useGetCandidatesValidQuery();
  const selectedCandidate = candidates?.find((candidate) => candidate.id === candidateId);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    selectedCandidate?.correspondenceLanguage ?? Language.DE
  );

  const {
    data: availableExamSession,
    refetch,
    isFetching: isFetchingExams,
  } = useGetExamSessionAvailableForSignUpQuery(
    {
      examSessionId,
      candidateId: selectedCandidate === undefined ? "" : selectedCandidate.id,
      language: selectedLanguage,
    },
    {
      skip: selectedCandidate === undefined,
    }
  );

  useEffect(() => {
    setSelectedLanguage(selectedCandidate?.correspondenceLanguage ?? Language.DE);
  }, [selectedCandidate]);

  const handleLanguageChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const language = event.target.value as Language;
    setSelectedLanguage(language);
    refetch();
  };

  if (isFetching) {
    return <LinearProgress variant="query" />;
  }

  if (candidates === undefined) {
    return <></>;
  }

  const candidateOptions: OptionType[] = candidates.map((candidate) => ({
    label: `${candidate.firstName} ${candidate.lastName} - ${candidate.emailAddress}`,
    value: candidate.id,
    payload: candidate,
  }));

  const selectedCandidateOption = candidateOptions.find((option) => option.value === candidateId);

  const isCourseDisabled = selectedCandidateOption === undefined || availableExamSession === undefined;
  const courseOptions: OptionType[] = !isCourseDisabled
    ? availableExamSession.courses.map((course) => ({
        label: GetTranslatedValueIntl(intl, course.name),
        value: course.id,
        payload: course,
      }))
    : [];
  const selectedCourseOption = courseOptions.find((option) => option.value === courseId);

  const isModuleDisabled = selectedCourseOption === undefined;
  const moduleOptions: OptionType[] = !isModuleDisabled
    ? (selectedCourseOption.payload! as CourseForSignupViewModel).modules.map((module) => ({
        label: GetTranslatedValueIntl(intl, module.name),
        value: module.id,
        payload: module,
      }))
    : [];
  const selectedModuleOption = moduleOptions.find((option) => option.value === moduleId);

  const submit = () => {
    let signup: ModuleForSignupViewModel = selectedModuleOption!.payload;
    signup = { ...signup, bookingWithInvoice: bookingWithInvoice, language: selectedLanguage };
    add(candidateId!, signup);
    reset();
  };

  const handleAccept = () => {
    handleSubmit(submit)();
  };

  const handleCancel = () => {
    reset();
    cancel();
  };

  return (
    <CustomDraggableDialog
      acceptText={<TranslatedMessage id="common.accept" />}
      cancelText={<TranslatedMessage id="common.cancel" />}
      content={
        <form className={classes.form}>
          <Controller
            control={control}
            name="candidateId"
            render={({ field: { onChange }, fieldState: { invalid } }) => {
              return (
                <CustomSelect
                  className={classes.candidateSelect}
                  name="candidateId"
                  label={<TranslatedMessage id="examSessionCandidate.addDialog.candidate" />}
                  placeholder=""
                  options={candidateOptions}
                  isMulti={false}
                  required
                  error={invalid}
                  value={selectedCandidateOption || null}
                  onChange={(value) => {
                    resetField("courseId");
                    resetField("moduleId");
                    onChange((value as OptionType).value);
                  }}
                />
              );
            }}
          />
          {selectedCandidate && (
            <FormGridCell size="wide" className={classes.languageCell}>
              <>
                <InputLabel>
                  <TranslatedMessage id="examSessionCandidate.bookingDialog.selectLanguage" />
                </InputLabel>
                <Select value={selectedLanguage} onChange={handleLanguageChange} className={classes.select}>
                  {Object.keys(Language).map((languageKey) => {
                    const lang = Language[languageKey as Language];
                    return (
                      <MenuItem key={lang} value={lang}>
                        {LanguageLabels[lang]}
                      </MenuItem>
                    );
                  })}
                </Select>
              </>
            </FormGridCell>
          )}
          <Controller
            control={control}
            name="courseId"
            render={({ field: { onChange }, fieldState: { invalid } }) => {
              return (
                <CustomSelect
                  name="courseId"
                  label={<TranslatedMessage id="examSessionCandidate.addDialog.course" />}
                  placeholder=""
                  options={isCourseDisabled ? [] : courseOptions}
                  isMulti={false}
                  isDisabled={isCourseDisabled}
                  required
                  error={invalid}
                  value={selectedCourseOption || null}
                  onChange={(value) => onChange((value as OptionType).value)}
                />
              );
            }}
          />
          <Controller
            control={control}
            name="moduleId"
            render={({ field: { onChange }, fieldState: { invalid } }) => {
              return (
                <CustomSelect
                  name="moduleId"
                  label={<TranslatedMessage id="examSessionCandidate.addDialog.module" />}
                  placeholder=""
                  options={isModuleDisabled ? [] : moduleOptions}
                  isMulti={false}
                  isDisabled={isModuleDisabled}
                  required
                  error={invalid}
                  value={selectedModuleOption || null}
                  onChange={(value) => onChange((value as OptionType).value)}
                />
              );
            }}
          />
          <Controller
            control={control}
            name="bookingWithInvoice"
            render={({ field: { onChange } }) => {
              return (
                <div className={classes.checkbox}>
                  <Typography>
                    <TranslatedMessage id="examSessionCandidate.addDialog.bookingWithInvoice" />
                  </Typography>
                  <Switch color="primary" checked={bookingWithInvoice} onChange={(value) => onChange(value)} />
                </div>
              );
            }}
          />
          <LoadingIndicator show={isFetchingExams} />
        </form>
      }
      isOpen={isOpen}
      onAccept={handleAccept}
      onCancel={handleCancel}
      title={<TranslatedMessage id="examSessionCandidate.addDialog.title" />}
    />
  );
};

export default ExamSessionCandidateAddDialog;
